// TODO(dev:dashboard_v2, translations): Add missing translations
import React, { useState, useEffect, useMemo } from "react"
import { cn } from "~/lib/utils"
import { Tag, TagIcon, TagLabel } from "~/react/ui/tag"
import { Icon } from "~/react/ui/icon"
import { Button, ButtonSuffix } from "~/react/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "~/react/ui/sheet"
import {
  clearFilters,
  filterFunction,
  Filters,
  FiltersType,
  LocationsMappingsType,
  sortFunction,
} from "~/react/components/search/filters"
import EmailsList from "~/react/components/search/emails-list"
import { leads_path } from "~/lib/routes"
import { Snackbar, SnackbarText, SnackbarLink } from "../ui/snackbar"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { useLocationMappings } from "../hooks/useLocationMappings"
import { useEmailSearch } from "../hooks/useEmailSearch"
import { useEmailsLoading } from "../hooks/useEmailsLoading"
import { TabSelected } from "../types/tabSelected"
import { useCampaignsSave } from "../hooks/useCampaignsSave"
import { useLeadsListSave } from "../hooks/useLeadsListSave"
import { useJobTitleSuggestions } from "../hooks/useJobTitleSuggestions"
import { useSplittedResults } from "../hooks/useSplittedResults"
import { useRevealCost } from "../hooks/useRevealCost"
import { LeadSaveManager } from "./lead-save-manager"
import { useCampaigns } from "./campaigns-context"
import { useLeadsLists } from "./leads-lists-context"
import { OpportunityCompaniesSidepanel } from "./opportunity-companies-sidepanel"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuDivider,
  DropdownMenuHint,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"

type SignalType = "hunter-suggestion" | "new-result"
type EntityType = "lead" | "company"

function OpportunityPeopleSidepanel({
  id,
  emailIds,
  campaigns,
  setCampaigns,
  leadsLists,
  setLeadsLists,
  numberOfCreditsLeft,
  isOpen,
  onOpenChange,
}: {
  id: string
  emailIds: number[]
  numberOfCreditsLeft: number
  campaigns: { id: number; name: string }[]
  setCampaigns: (campaigns: { id: number; name: string }[]) => void
  leadsLists: { id: number; name: string }[]
  setLeadsLists: (leadsLists: { id: number; name: string }[]) => void
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}): React.ReactNode {
  const [emailsToLoad, setEmailsToLoad] = useState<number[]>([])
  const [filters, setFilters] = useState<FiltersType>({
    requiredFields: [],
    departments: [],
    jobTitles: [],
    verificationStatus: [],
    hideAlreadySaved: false,
    location: [],
    peoplePerCompany: undefined,
  })
  const locationsMappings: LocationsMappingsType = useLocationMappings()
  const {
    results,
    setResults,
    revealingIds,
    revealedIds,
    selectedIds,
    handleReveal,
    handleRevealMultiple,
    handleSelect,
  } = useEmailSearch({
    initialEmails: [],
    context: `dashboard-${id}`,
    source: "dashboard",
  })
  const handleCampaignsSave = useCampaignsSave(results, campaigns, setCampaigns)
  const handleLeadsListSave = useLeadsListSave(results, setLeadsLists, setResults)
  const [tabSelected, setTabSelected] = useState<TabSelected>("all_people")
  const { isLoading } = useEmailsLoading(emailsToLoad, setResults)
  const revealAllButtonRef = React.useRef(null)

  useEffect(() => {
    if (isOpen && emailsToLoad.length === 0) {
      setEmailsToLoad(emailIds)
    }
  }, [isOpen, emailIds, emailsToLoad.length])
  const jobTitles = useJobTitleSuggestions(results, filters, locationsMappings, revealedIds)
  const filteredResults = useMemo(() => {
    const baseFilteredResults = filterFunction(results, filters, locationsMappings, revealedIds)

    return baseFilteredResults.sort(sortFunction)
  }, [results, filters, revealedIds])
  const { allPeopleResults, decisionMakersResults, genericResults } = useSplittedResults(filteredResults)
  const { revealAllOperationCost } = useRevealCost({
    selectedIds,
    tabSelected,
    results,
    allPeopleResults,
    decisionMakersResults,
    genericResults,
    numberOfCreditsLeft,
  })
  const revealCurrentTab = (leadsListId?: number) => {
    const idsToReveal =
      tabSelected === "all_people"
        ? allPeopleResults.map((result) => result.id)
        : tabSelected === "decision_makers"
          ? decisionMakersResults.map((result) => result.id)
          : tabSelected === "generic"
            ? genericResults.map((result) => result.id)
            : []
    handleRevealMultiple(idsToReveal)
  }
  const handleLeadManagerSave = async (name: string, listId: number) => {
    const listAlreadyExists = leadsLists.find((list) => list.id === listId)

    if (!listAlreadyExists) {
      setLeadsLists((prev) => [...prev, { id: listId, name }])
    }

    if (selectedIds.length > 0) {
      handleRevealMultiple(selectedIds, { leads_list_id: listId })
    } else {
      revealCurrentTab(listId)
    }
  }
  const anyFiltersSet =
    filters.departments.length > 0 ||
    filters.jobTitles.length > 0 ||
    filters.requiredFields.length > 0 ||
    filters.hideAlreadySaved ||
    filters.verificationStatus.length > 0 ||
    filters.location.length > 0

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="default" size="xs">
          Show people
        </Button>
      </SheetTrigger>
      <SheetContent size="lg" className="p-0">
        <SheetHeader>
          <SheetTitle>Discover new leads</SheetTitle>
        </SheetHeader>
        <Filters
          possibleFilters={[
            "department",
            "job_title",
            "already_saved",
            "verification_status",
            "required_field",
            "location",
            "people_per_company",
          ]}
          jobTitles={jobTitles}
          anyFiltersSet={anyFiltersSet}
          clearFilters={() => clearFilters(setFilters)}
          setFilters={setFilters}
          filters={filters}
        />
        <EmailsList
          results={filteredResults}
          anyFiltersSet={anyFiltersSet}
          selectedIds={selectedIds}
          campaigns={campaigns}
          leadsLists={leadsLists}
          handleReveal={handleReveal}
          handleLeadsListSave={handleLeadsListSave}
          handleCampaignsSave={handleCampaignsSave}
          handleSelect={handleSelect}
          showGenericAlert={false}
          isLoading={isLoading}
          onTabSelected={setTabSelected}
          clearFilters={() => clearFilters(setFilters)}
        />
        <SheetFooter sticky>
          {revealedIds.length > 0 && (
            <Snackbar>
              <Icon icon="check-circle" variant="solid" size="sm" className="text-success" />
              <SnackbarText>
                <strong className="fw-medium">
                  {i18n.t("js.multi_domain_search.footer.leads", {
                    count: revealedIds.length,
                    pretty_count: CurrencyHelper.numberWithDelimiter(revealedIds.length),
                  })}
                </strong>
                &nbsp;
                {i18n.t("js.multi_domain_search.footer.saved")}
              </SnackbarText>
              <SnackbarLink
                href={leads_path({
                  updated_at: "last1h",
                })}
                turboFrame="_top"
              >
                {i18n.t("js.multi_domain_search.footer.see_in_leads")}
              </SnackbarLink>
            </Snackbar>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="primary" data-bs-toggle="dropdown" data-bs-auto-close="outside">
                {revealingIds.length > 0
                  ? i18n.t("js.multi_domain_search.footer.saving_leads")
                  : i18n.t("js.multi_domain_search.footer.save_leads", {
                      count: selectedIds.length,
                    })}
                <ButtonSuffix>
                  <span className="cost-indicator cost-indicator--bare">
                    <Icon icon="coin" size="xs" className="cost-indicator__icon" />
                    {CurrencyHelper.numberWithDelimiter(revealAllOperationCost)}
                  </span>
                </ButtonSuffix>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuHint>
                <Icon icon="coin" size="xs" className="cost-indicator__icon" />
                {i18n.t("js.multi_domain_search.footer.cost_tooltip", {
                  count: selectedIds.length,
                  cost: CurrencyHelper.numberWithDelimiter(revealAllOperationCost),
                })}
              </DropdownMenuHint>
              <DropdownMenuDivider />
              <DropdownMenuItem>
                <LeadSaveManager
                  className="w-100"
                  id="multi-domain-search"
                  leadsLists={leadsLists}
                  onSave={handleLeadManagerSave}
                  disableButtons={revealingIds.length > 0}
                  cost={revealAllOperationCost}
                  hint={i18n.t("js.multi_domain_search.footer.existing_leads_hint", { count: 3 })}
                  customPosition={(triggerElement) => {
                    const dropdownMenu = triggerElement.nextElementSibling as HTMLElement
                    if (!dropdownMenu?.classList.contains("dropdown-menu")) return

                    const parentDropdown = triggerElement.closest(".dropdown-menu") as HTMLElement
                    if (!parentDropdown) return

                    // Find all scrollable parents
                    const scrollableParents: HTMLElement[] = []
                    let element = parentDropdown.parentElement
                    while (element) {
                      const overflow = window.getComputedStyle(element).overflow
                      if (overflow === "auto" || overflow === "scroll" || overflow === "overlay") {
                        scrollableParents.push(element)
                      }
                      element = element.parentElement
                    }

                    const updatePosition = () => {
                      const parentRect = parentDropdown.getBoundingClientRect()

                      dropdownMenu.style.position = "fixed"
                      dropdownMenu.style.bottom = "0"
                      dropdownMenu.style.left = `${parentRect.width}px`
                      dropdownMenu.style.transform = "none"
                    }

                    // Initial position
                    updatePosition()

                    // Update on scroll and resize
                    window.addEventListener("scroll", updatePosition, true)
                    window.addEventListener("resize", updatePosition)
                    scrollableParents.forEach((parent) => {
                      parent.addEventListener("scroll", updatePosition)
                    })

                    // Cleanup when dropdown hides
                    const handleHide = () => {
                      window.removeEventListener("scroll", updatePosition, true)
                      window.removeEventListener("resize", updatePosition)
                      scrollableParents.forEach((parent) => {
                        parent.removeEventListener("scroll", updatePosition)
                      })
                      triggerElement.removeEventListener("hidden.bs.dropdown", handleHide)
                    }

                    triggerElement.addEventListener("hidden.bs.dropdown", handleHide)
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    {i18n.t("js.multi_domain_search.footer.save_in_a_specific_list")}
                    <Icon icon="angle-right" size="sm" aria-hidden="true" />
                  </div>
                </LeadSaveManager>
              </DropdownMenuItem>
              <li>
                <DropdownMenuItem asChild closeOnClick>
                  <button
                    ref={revealAllButtonRef}
                    disabled={revealingIds.length > 0}
                    onClick={() => {
                      if (selectedIds.length > 0) {
                        handleRevealMultiple(selectedIds)
                      } else {
                        revealCurrentTab()
                      }
                    }}
                    className="dropdown-item"
                  >
                    {i18n.t("js.multi_domain_search.footer.save_in_leads")}
                  </button>
                </DropdownMenuItem>
              </li>
            </DropdownMenuContent>
          </DropdownMenu>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

interface OpportunitySignalItemProps extends React.HTMLAttributes<HTMLDivElement> {
  id?: number
  title: string
  visited: boolean
  description: string
  numberOfCreditsLeft: number
  type: SignalType
  entityType: EntityType
  lookalikeIds: number[]
  entityCount: number
  onClick?: () => void
}

const OpportunitySignalItem = React.forwardRef<HTMLDivElement, OpportunitySignalItemProps>(
  (
    {
      className,
      id,
      title,
      visited,
      description,
      numberOfCreditsLeft,
      type,
      entityType,
      lookalikeIds,
      entityCount,
      onClick,
      ...props
    },
    ref,
  ) => {
    const { campaigns, setCampaigns } = useCampaigns()
    const { leadsLists, setLeadsLists } = useLeadsLists()
    const [isSidepanelOpen, setIsSidepanelOpen] = useState(false)

    const renderTypeTag = () => {
      switch (type) {
        case "hunter-suggestion":
          return (
            <Tag size="md" variant="ai">
              <TagIcon>
                <Icon icon="sparkle" size="sm" />
              </TagIcon>
              <TagLabel>Hunter suggestion</TagLabel>
            </Tag>
          )
        case "new-result":
          return (
            <Tag size="md" variant="info">
              <TagIcon>
                <Icon icon="bookmark" size="sm" />
              </TagIcon>
              <TagLabel>New result</TagLabel>
            </Tag>
          )
      }
    }

    const renderEntityTag = () => {
      switch (entityType) {
        case "lead":
          return (
            <Tag size="md">
              <TagIcon>
                <Icon icon="users" size="sm" opticalCorrection />
              </TagIcon>
              <TagLabel>{entityCount} people</TagLabel>
            </Tag>
          )
        case "company":
          return (
            <Tag size="md">
              <TagIcon>
                <Icon icon="buildings" size="sm" />
              </TagIcon>
              <TagLabel>{entityCount} companies</TagLabel>
            </Tag>
          )
      }
    }

    return (
      <article ref={ref} className={cn("opportunity-signal-item", className)} {...props}>
        <button
          className="opportunity-signal-item__link"
          onClick={() => {
            if (id !== undefined) {
              setIsSidepanelOpen(true)
            }
            onClick?.()
          }}
          aria-label={`View opportunity: ${title}`}
        >
          <span className="sr-only">{`View opportunity: ${title}`}</span>
        </button>
        <div className="opportunity-signal-item__content">
          <div className="opportunity-signal-item__header">
            <span className="opportunity-signal-item__title">{title}</span>

            {!visited && (
              <Tag size="sm" variant="info">
                <TagLabel>New</TagLabel>
              </Tag>
            )}
          </div>
          <span className="opportunity-signal-item__description">{description}</span>
        </div>
        <div className="opportunity-signal-item__footer">
          <div className="opportunity-signal-item__badges">
            {renderTypeTag()} {renderEntityTag()}
          </div>
          {id !== undefined && entityType === "lead" && (
            <OpportunityPeopleSidepanel
              id={id.toString()}
              emailIds={lookalikeIds}
              numberOfCreditsLeft={numberOfCreditsLeft}
              campaigns={campaigns}
              leadsLists={leadsLists}
              setCampaigns={setCampaigns}
              setLeadsLists={setLeadsLists}
              isOpen={isSidepanelOpen}
              onOpenChange={setIsSidepanelOpen}
            />
          )}
          {id !== undefined && entityType === "company" && (
            <OpportunityCompaniesSidepanel
              id={id.toString()}
              isOpen={isSidepanelOpen}
              onOpenChange={setIsSidepanelOpen}
              domainIds={lookalikeIds}
            />
          )}
          {id === undefined && (
            <Button variant="default" size="xs">
              {entityType === "lead" ? "Show people" : "Show companies"}
            </Button>
          )}
        </div>
      </article>
    )
  },
)

OpportunitySignalItem.displayName = "OpportunitySignalItem"

export { OpportunitySignalItem }
